/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import { isPanelTeamMember } from '../../panel/panelAccess/panelRoleAccess';
import type { Batiment, Vehicule, Personnel } from '../../../shared/types/game';
import { useMapData } from '../map/hooks/useMapData';
import { useInterventions } from '../map/hooks/useInterventions';
import { getApiBaseUrl } from '../../../core/config/api';
import { useVehicleCrewStatus } from '../map/hooks/useVehicleCrewStatus';
import { useStatusPalette } from '../map/hooks/useStatusPalette';
import WorldMap from '../components/WorldMap';
import { useMapSocket } from '../map/hooks/useMapSocket';
import RadioJournalDispatch from '../components/interventions/RadioJournalDispatch';
import {
  getInterventionDisplayLines,
  getInterventionPhaseUiKey,
  effectiveMoyensRequired,
  isTerminalInterventionStatus,
  type InstanceRow,
} from '../components/interventions/instanceModel';
import CockpitTopbar, { type Scope, type CtaOption } from './CockpitTopbar';
import CockpitRail, { OPS_KEYS } from './CockpitRail';
import CockpitDrawer from './CockpitDrawer';
import CommandPalette, { type PaletteItem } from './CommandPalette';
import UnitsTable from './UnitsTable';
import ContextPanel from './ContextPanel';
// Écrans de gestion : on réutilise les vues existantes plutôt que des placeholders vides.
import { useMapMetrics } from '../map/hooks/useMapMetrics';
import MoyensSynopticView from '../map/views/MoyensSynopticView';
import ReseauView from '../map/views/ReseauView';
import HistoriqueView from '../map/views/HistoriqueView';
import BuildingTrameEditor from '../map/views/BuildingTrameEditor';
import BuildingDetailModal from '../components/BuildingDetailModal';
// Contenus de tiroirs « Gestion » (ouverts par-dessus le cockpit, fidèles au prototype).
import SitesDrawer from './drawers/SitesDrawer';
import AlliancesDrawer from './drawers/AlliancesDrawer';
import StatsDrawer from './drawers/StatsDrawer';
import AideDrawer from './drawers/AideDrawer';
import ReglagesDrawer from './drawers/ReglagesDrawer';
import TechnosDrawer from './drawers/TechnosDrawer';
// Multi-écrans (port littéral maître/réplique du prototype).
import { useCockpitSync } from './sync/useCockpitSync';
import ScreensControl from './sync/ScreensControl';
import { getScreenFromUrl, openScreen } from './sync/screens';
import './proto.css';

/** Titres des tiroirs de gestion — repris du prototype (`DRAWER_TITRES`). */
const DRAWER_TITLES: Record<string, string> = {
  sites: 'Implantations et sites',
  reseau: 'Réseau — parc et personnel',
  planning: 'Planning du personnel',
  formations: 'Formations et spécialités',
  technologies: 'Arbre technologique',
  alliances: 'Alliances',
  stats: 'Statistiques et profil de build',
  succes: 'Succès',
  reglages: 'Réglages',
  aide: 'Aide en jeu',
};

/**
 * Port fidèle du prototype (`prototype-cta/`) dans l'app officielle : mêmes
 * visuels (CSS scopée sous `.oscockpit`), même structure DOM, mêmes fonctions —
 * mais branché sur les VRAIES données du joueur, sans aucun automatisme.
 * Les interventions ne se créent pas toutes seules : c'est la prise d'appels
 * (OFF par défaut) et le joueur qui les pilotent.
 */

type QueueFilter = 'wait' | 'run' | 'all';

/** État de référence diffusé par le maître aux répliques (port de `sync.js`). */
interface SyncPayload {
  interventions: InstanceRow[];
  buildings: Batiment[];
  playerVehicles: Vehicule[];
  playerPersonnel: Personnel[];
  liveVehicleTracks: any;
  priseAppels: boolean;
  scope: Scope;
  selectedId: number | null;
}

const Jeu: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const screen = useMemo(() => getScreenFromUrl(), []);

  const [, setContextMenu] = useState<any>(null);
  const {
    buildings: ownBuildings,
    playerVehicles: ownPlayerVehicles,
    playerPersonnel: ownPlayerPersonnel,
  } = useMapData({ currentUser, setContextMenu });
  const { interventions: ownInterventions, setInterventions, loadInterventions } = useInterventions({ currentUser, authHeaders });
  const [ownPriseAppels, setPriseAppels] = useState(false);
  const { liveVehicleTracks: ownLiveVehicleTracks } = useMapSocket({
    userId: currentUser?.id,
    setInterventions,
    setPriseAppels,
    loadInterventions,
  });
  const { vehicles: crewStatus, staffById } = useVehicleCrewStatus();
  const { palette } = useStatusPalette();

  const [ownSelectedId, setSelectedId] = useState<number | null>(null);
  const [queueFilter, setQueueFilter] = useState<QueueFilter>('wait');
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<number>>(new Set());
  const [engaging, setEngaging] = useState(false);
  const [engageError, setEngageError] = useState<string | null>(null);

  /**
   * Engage les engins sélectionnés sur l'intervention ciblée (réutilise la route de dispatch
   * `POST /api/cta/appels/creer-intervention`, préfixe `intervention-<id>` = engagement sur une
   * instance existante). Le bouton « Engager » du moniteur d'engins était un port visuel sans action.
   */
  const engageUnits = useCallback(
    async (interventionId: number, vehicleIds: number[]) => {
      const uniq = vehicleIds.filter((id, i, arr) => Number.isInteger(id) && id > 0 && arr.indexOf(id) === i);
      if (!interventionId || uniq.length === 0) return;
      setEngaging(true);
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/cta/appels/creer-intervention`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            appel_id: `intervention-${interventionId}`,
            vehicules: uniq.map((vehicule_id) => ({ vehicule_id, personnel_ids: [] })),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.success) {
          setEngageError(data?.error || 'Échec de l’engagement.');
          return;
        }
        const nonAssignes = (data.data?.vehicules_non_assignes || []) as Array<{ vehicule_id: number; raison?: string }>;
        const raisons = Array.from(new Set(nonAssignes.map((v) => v.raison).filter((r): r is string => Boolean(r))));
        setEngageError(
          nonAssignes.length > 0
            ? `${nonAssignes.length} engin(s) non engagé(s)${raisons.length ? ' : ' + raisons.join(' ; ') : ''}.`
            : null,
        );
        setSelectedUnitIds(new Set());
        await loadInterventions();
      } catch {
        setEngageError('Erreur réseau lors de l’engagement.');
      } finally {
        setEngaging(false);
      }
    },
    [authHeaders, loadInterventions],
  );
  const [railActive, setRailActive] = useState(
    screen === 'synoptique' ? 'synoptique' : screen === 'journal' ? 'main-courante' : 'dispatch',
  );
  const [drawerKey, setDrawerKey] = useState<string | null>(null);
  const [ownScope, setScope] = useState<Scope>('all');
  const [mapNight, setMapNight] = useState(true);
  const [showSectors, setShowSectors] = useState(false);
  const [detailBuildingId, setDetailBuildingId] = useState<number | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ── Multi-écrans (maître/réplique) : port littéral du prototype (sync.js).
  //    Le maître diffuse son état ; une réplique l'applique (et suit sélection
  //    + périmètre si « follow »). NB : le serveur reste la vraie source de
  //    vérité ; cette couche reproduit le comportement multi-fenêtres du proto.
  const [follow, setFollow] = useState(true);
  const [received, setReceived] = useState<SyncPayload | null>(null);
  const sync = useCockpitSync({ onState: (p) => setReceived(p as SyncPayload) });
  const { isMaster, broadcastState } = sync;

  const replicaActive = !isMaster && received != null;
  const replicaFollowing = replicaActive && follow;

  const buildings = replicaActive ? received!.buildings : ownBuildings;
  const playerVehicles = replicaActive ? received!.playerVehicles : ownPlayerVehicles;
  const playerPersonnel = replicaActive ? received!.playerPersonnel : ownPlayerPersonnel;
  const interventions = replicaActive ? received!.interventions : ownInterventions;
  const liveVehicleTracks = replicaActive ? received!.liveVehicleTracks : ownLiveVehicleTracks;
  const priseAppels = replicaActive ? received!.priseAppels : ownPriseAppels;
  const scope = replicaFollowing ? received!.scope : ownScope;
  const selectedId = replicaFollowing ? received!.selectedId : ownSelectedId;

  // Le maître diffuse son état de référence à chaque changement significatif.
  React.useEffect(() => {
    if (!isMaster) return;
    broadcastState({
      interventions: ownInterventions,
      buildings: ownBuildings,
      playerVehicles: ownPlayerVehicles,
      playerPersonnel: ownPlayerPersonnel,
      liveVehicleTracks: ownLiveVehicleTracks,
      priseAppels: ownPriseAppels,
      scope: ownScope,
      selectedId: ownSelectedId,
    });
  }, [
    isMaster,
    broadcastState,
    ownInterventions,
    ownBuildings,
    ownPlayerVehicles,
    ownPlayerPersonnel,
    ownLiveVehicleTracks,
    ownPriseAppels,
    ownScope,
    ownSelectedId,
  ]);

  const onRailNavigate = useCallback(
    (key: string) => {
      if (key === 'dashboard') return navigate('/dashboard');
      if (key === 'panel') return navigate('/panel');
      // Vue « Ops » → workspace ; on referme tout tiroir ouvert.
      if (OPS_KEYS.includes(key)) {
        setRailActive(key);
        setDrawerKey(null);
        return;
      }
      // Item « Gestion » / bas → tiroir overlay (toggle), le cockpit reste derrière.
      setDrawerKey((cur) => (cur === key ? null : key));
    },
    [navigate],
  );
  const closeDrawer = useCallback(() => setDrawerKey(null), []);

  const activeInterventions = useMemo(
    () => (interventions || []).filter((i) => !isTerminalInterventionStatus(i.statut || '')),
    [interventions],
  );
  const selected = useMemo(
    () => activeInterventions.find((i) => i.id === selectedId) || null,
    [activeInterventions, selectedId],
  );

  // ── Périmètre (scope) : un « centre d'appel » = bâtiment de type CTA.
  //    Un bâtiment est dans le périmètre s'il EST le CTA sélectionné ou s'il y est
  //    rattaché (cta_id). Un engin suit le CTA de son bâtiment. Une intervention
  //    suit le CTA des engins engagés ; celles sans engin (en attente) restent
  //    visibles partout (pool partagé, non encore rattaché à un centre).
  const buildingById = useMemo(() => {
    const m = new Map<number, (typeof buildings)[number]>();
    buildings.forEach((b) => m.set(Number(b.id), b));
    return m;
  }, [buildings]);
  const isCtaType = (b?: { type?: string } | null) => String(b?.type ?? '').trim().toLowerCase() === 'cta';
  const ctaOfBuilding = useCallback(
    (b?: { id?: number; type?: string; cta_id?: number | null } | null): number | null => {
      if (!b) return null;
      if (isCtaType(b)) return Number(b.id);
      return b.cta_id != null ? Number(b.cta_id) : null;
    },
    [],
  );
  const vehicleCtaById = useMemo(() => {
    const m = new Map<number, number | null>();
    playerVehicles.forEach((v) => m.set(Number(v.id), ctaOfBuilding(buildingById.get(Number(v.batiment_id)))));
    return m;
  }, [playerVehicles, buildingById, ctaOfBuilding]);

  const buildingInScope = useCallback(
    (b: { id?: number; cta_id?: number | null }) => scope === 'all' || Number(b.id) === scope || Number(b.cta_id) === scope,
    [scope],
  );
  const vehicleInScope = useCallback(
    (v: { id?: number }) => scope === 'all' || vehicleCtaById.get(Number(v.id)) === scope,
    [scope, vehicleCtaById],
  );
  const interventionInScope = useCallback(
    (i: InstanceRow) => {
      if (scope === 'all') return true;
      const ids = (i as any).dispatch_player_vehicle_ids as number[] | undefined;
      if (!Array.isArray(ids) || ids.length === 0) return true; // pool partagé (en attente)
      return ids.some((id) => vehicleCtaById.get(Number(id)) === scope);
    },
    [scope, vehicleCtaById],
  );

  const ctaList = useMemo<CtaOption[]>(() => {
    const shortCode = (nom?: string) => {
      const words = String(nom ?? '').trim().split(/\s+/).filter(Boolean);
      if (!words.length) return 'CTA';
      return (words.map((w) => w[0]).join('').slice(0, 4).toUpperCase()) || 'CTA';
    };
    return buildings
      .filter((b) => isCtaType(b))
      .map((c) => ({
        id: Number(c.id),
        nom: c.nom || `CTA ${c.id}`,
        ind: shortCode(c.nom),
        sites: buildings.filter((b) => Number(b.cta_id) === Number(c.id)).length,
        engins: playerVehicles.filter((v) => vehicleCtaById.get(Number(v.id)) === Number(c.id)).length,
        actif: Boolean(c.actif),
      }));
  }, [buildings, playerVehicles, vehicleCtaById]);

  const scopedBuildings = useMemo(
    () => (scope === 'all' ? buildings : buildings.filter(buildingInScope)),
    [buildings, scope, buildingInScope],
  );
  const scopedVehicles = useMemo(
    () => (scope === 'all' ? playerVehicles : playerVehicles.filter(vehicleInScope)),
    [playerVehicles, scope, vehicleInScope],
  );
  const scopedPersonnel = useMemo(
    () =>
      scope === 'all'
        ? playerPersonnel
        : playerPersonnel.filter((p) => {
            const b = buildingById.get(Number(p.batiment_id));
            return b ? buildingInScope(b) : false;
          }),
    [playerPersonnel, scope, buildingById, buildingInScope],
  );
  const scopedActiveInterventions = useMemo(
    () => (scope === 'all' ? activeInterventions : activeInterventions.filter(interventionInScope)),
    [activeInterventions, scope, interventionInScope],
  );
  const scopedInterventions = useMemo(
    () => (scope === 'all' ? interventions : (interventions || []).filter(interventionInScope)),
    [interventions, scope, interventionInScope],
  );
  const scopeCount = useMemo(
    () => `${scopedBuildings.filter((b) => !isCtaType(b)).length} sites · ${scopedVehicles.length} engins`,
    [scopedBuildings, scopedVehicles],
  );

  // Si l'intervention sélectionnée sort du périmètre, on désélectionne.
  // (Inerte sur une réplique qui suit le maître : la sélection y vient du maître.)
  React.useEffect(() => {
    if (replicaFollowing) return;
    if (selectedId != null && !scopedActiveInterventions.some((i) => i.id === selectedId)) {
      setSelectedId(null);
    }
  }, [replicaFollowing, selectedId, scopedActiveInterventions]);

  const counts = useMemo(() => {
    const wait = scopedActiveInterventions.filter((i) => getInterventionPhaseUiKey(i) === 'wait').length;
    const run = scopedActiveInterventions.filter((i) => ['travel', 'site'].includes(getInterventionPhaseUiKey(i))).length;
    return { wait, run, all: scopedActiveInterventions.length };
  }, [scopedActiveInterventions]);

  const shownQueue = useMemo(() => {
    if (queueFilter === 'wait') return scopedActiveInterventions.filter((i) => getInterventionPhaseUiKey(i) === 'wait');
    if (queueFilter === 'run') return scopedActiveInterventions.filter((i) => ['travel', 'site'].includes(getInterventionPhaseUiKey(i)));
    return scopedActiveInterventions;
  }, [scopedActiveInterventions, queueFilter]);

  const dispoCount = scopedVehicles.filter((v) => v.statut === 'disponible').length;
  const engagedCount = scopedActiveInterventions.reduce((n, i) => {
    const ids = (i as any).dispatch_player_vehicle_ids;
    return n + (Array.isArray(ids) ? ids.length : 0);
  }, 0);

  const elapsed = (inv: InstanceRow): string => {
    const start = inv.date_creation ? new Date(inv.date_creation).getTime() : NaN;
    if (!Number.isFinite(start)) return '00:00';
    const s = Math.max(0, Math.floor((Date.now() - start) / 1000));
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  };

  // Métriques bâtiments (réutilisées par l'écran Sites / BuildingsView).
  const metrics = useMapMetrics({
    buildings,
    playerVehicles,
    playerPersonnel,
    activeInterventions,
    selectedIntervention: selected,
  });
  const detailBuilding = useMemo(
    () => (detailBuildingId == null ? null : buildings.find((b) => Number(b.id) === detailBuildingId) ?? null),
    [detailBuildingId, buildings],
  );

  // ── Palette de commandes (Ctrl+K) — port de `app.js` buildPalette.
  const paletteItems = useMemo<PaletteItem[]>(() => {
    const goView = (v: string) => {
      setRailActive(v);
      setDrawerKey(null);
    };
    const cmds: PaletteItem[] = [
      { ico: '🚨', t: 'Aller au dispatch', k: '1', run: () => goView('dispatch') },
      { ico: '📊', t: 'Ouvrir le synoptique', k: '2', run: () => goView('synoptique') },
      { ico: '📝', t: 'Ouvrir la main courante', k: '3', run: () => goView('main-courante') },
      { ico: '🏢', t: 'Gestion — Sites et implantations', run: () => setDrawerKey('sites') },
      { ico: '🚒', t: 'Gestion — Réseau (parc et personnel)', run: () => setDrawerKey('reseau') },
      { ico: '🗓️', t: 'Gestion — Planning du personnel', run: () => setDrawerKey('planning') },
      { ico: '🎓', t: 'Gestion — Formations et spécialités', run: () => setDrawerKey('formations') },
      { ico: '🔬', t: 'Gestion — Technologies', run: () => setDrawerKey('technologies') },
      { ico: '🤝', t: 'Gestion — Alliances', run: () => setDrawerKey('alliances') },
      { ico: '📈', t: 'Statistiques et profil de build', run: () => setDrawerKey('stats') },
      { ico: '🏆', t: 'Succès', run: () => setDrawerKey('succes') },
      { ico: '⚙️', t: 'Réglages', run: () => setDrawerKey('reglages') },
      { ico: '❓', t: "Ouvrir l'aide en jeu", k: '?', run: () => setDrawerKey('aide') },
      { ico: '📞', t: `Prise d'appels : ${priseAppels ? 'désactiver' : 'activer'}`, run: () => setPriseAppels((v) => !v) },
      { ico: '🏠', t: 'Retour au tableau de bord', run: () => navigate('/dashboard') },
    ];
    if (isPanelTeamMember(Number(currentUser?.roles_id ?? 99))) {
      cmds.push({ ico: '🛠️', t: 'Ouvrir le panel', k: 'P', run: () => navigate('/panel') });
    }

    const scopes: PaletteItem[] = [
      { ico: '🌍', t: "Périmètre : tous les centres d'appel", k: 'Périmètre', run: () => setScope('all') },
      ...ctaList.map((c) => ({
        ico: '📞',
        t: `Périmètre : ${c.ind} — ${c.nom}`,
        k: 'Périmètre',
        run: () => setScope(c.id),
      })),
    ];

    const ints: PaletteItem[] = scopedActiveInterventions.map((it) => {
      const lines = getInterventionDisplayLines(it, 'Intervention');
      return {
        ico: '🔴',
        t: `${it.numero_intervention || it.id} · ${lines.headline}${lines.subline ? ` — ${lines.subline}` : ''}`,
        k: 'Interv.',
        run: () => {
          setSelectedId(it.id);
          goView('dispatch');
        },
      };
    });

    const uns: PaletteItem[] = scopedVehicles.map((v) => ({
      ico: '🚐',
      t: `${v.nom || v.type || `Engin ${v.id}`} · ${v.type || ''} — ${v.statut}`,
      k: 'Engin',
      run: () => {
        setSelectedUnitIds(new Set([Number(v.id)]));
        goView('dispatch');
      },
    }));

    const sites: PaletteItem[] = metrics.buildingOperationalRows.map((b) => ({
      ico: '🏢',
      t: `${b.name} · ${b.typeLabel} — niv. ${b.level}, ${b.personnelCount} agent(s)`,
      k: 'Site',
      run: () => setDetailBuildingId(Number(b.id)),
    }));

    const agents: PaletteItem[] = playerPersonnel.map((p) => {
      const b = buildingById.get(Number(p.batiment_id));
      const nom = `${p.prenom || ''} ${p.nom || ''}`.trim() || `Agent ${p.id}`;
      return {
        ico: '👤',
        t: `${nom}${b ? ` — ${b.nom || ''}` : ''}`,
        k: 'Agent',
        run: () => {
          if (b) setDetailBuildingId(Number(b.id));
        },
      };
    });

    return [...cmds, ...scopes, ...ints, ...uns, ...sites, ...agents];
  }, [
    priseAppels,
    currentUser,
    ctaList,
    scopedActiveInterventions,
    scopedVehicles,
    metrics.buildingOperationalRows,
    playerPersonnel,
    buildingById,
    navigate,
  ]);

  // Ctrl+K (ou Cmd+K) ouvre/ferme la palette de commandes.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /** Vues « Ops » affichées dans le workspace (le dispatch a son propre rendu). */
  const renderOpsView = (): React.ReactNode => {
    switch (railActive) {
      case 'synoptique':
        return <MoyensSynopticView playerVehicles={playerVehicles} buildings={buildings} />;
      case 'main-courante':
        return <HistoriqueView />;
      default:
        return null;
    }
  };

  /** Contenu des tiroirs « Gestion » — vraies vues DB, ouvertes par-dessus le cockpit. */
  const renderDrawer = (key: string): React.ReactNode => {
    switch (key) {
      case 'sites':
        return (
          <SitesDrawer
            buildings={buildings}
            rows={metrics.buildingOperationalRows}
            personnel={playerPersonnel}
            ctaList={ctaList}
            onOpenBuilding={(id) => setDetailBuildingId(id)}
          />
        );
      case 'reseau':
        return <ReseauView playerVehicles={playerVehicles} playerPersonnel={playerPersonnel} buildings={buildings} />;
      case 'planning':
        return (
          <BuildingTrameEditor
            buildings={metrics.buildingOperationalRows.map((b) => ({ id: b.id, name: b.name }))}
          />
        );
      case 'technologies':
        return <TechnosDrawer />;
      case 'stats':
        return (
          <StatsDrawer
            vehicles={metrics.vehicleStatusMetrics}
            personnel={metrics.personnelMetrics}
            breakdown={metrics.buildingTypeBreakdown}
            alerts={metrics.resourceAlerts}
            sitesCount={metrics.buildingOperationalRows.length}
          />
        );
      case 'alliances':
        return <AlliancesDrawer rolesId={currentUser?.roles_id} />;
      case 'aide':
        return <AideDrawer />;
      case 'reglages':
        return <ReglagesDrawer playerVehicles={playerVehicles} />;
      case 'formations':
        return (
          <div className="locked">
            <div className="locked__ico">🎓</div>
            <p className="locked__t">Formations & spécialités — bientôt</p>
            <p className="locked__d">
              La gestion des sessions de formation (recrutement des spécialités via
              <span className="mono"> config_competence.is_specialite</span>) n'est pas encore ouverte
              côté serveur. Le socle de compétences reste donné par le grade au recrutement.
            </p>
          </div>
        );
      case 'succes':
        return (
          <div className="locked">
            <div className="locked__ico">🏆</div>
            <p className="locked__t">Succès — bientôt</p>
            <p className="locked__d">
              Le système de succès du prototype n'a pas encore d'équivalent serveur (aucune table de
              progression). Il sera branché une fois la mécanique d'objectifs définie.
            </p>
          </div>
        );
      default:
        return (
          <div className="empty" style={{ margin: 24 }}>
            Tiroir « {DRAWER_TITLES[key] || key} » — port en cours.
          </div>
        );
    }
  };

  return (
    <div className={`oscockpit screen-${screen}`}>
      <CockpitTopbar
        currentUser={currentUser}
        priseAppels={priseAppels}
        onTogglePrise={() => setPriseAppels((v) => !v)}
        ctaList={ctaList}
        scope={scope}
        onScopeChange={setScope}
        scopeCount={scopeCount}
        screensSlot={
          <ScreensControl
            role={sync.role}
            screenCount={sync.screenCount}
            currentScreen={screen}
            peers={sync.peers}
            selfId={sync.id}
            available={sync.available}
            follow={follow}
            onToggleFollow={() => setFollow((v) => !v)}
            onOpenScreen={openScreen}
          />
        }
        onOpenPalette={() => setPaletteOpen(true)}
        onPanel={() => navigate('/panel')}
        onDashboard={() => navigate('/dashboard')}
      />

      <div className="shell">
        <CockpitRail
          active={railActive}
          openDrawer={drawerKey}
          onNavigate={onRailNavigate}
          showPanel={isPanelTeamMember(Number(currentUser?.roles_id ?? 99))}
        />

        <div className="workspace">
          <div className="ctxbar">
            <div className="ctxbar__path">
              <span className="ctxbar__mode">OPÉRATIONS</span>
              <span className="ctxbar__sep">▸</span>
              <span className="ctxbar__view">
                {railActive === 'synoptique' ? 'Synoptique' : railActive === 'main-courante' ? 'Main courante' : 'Dispatch'}
              </span>
              {selected ? (
                <>
                  <span className="ctxbar__sep">▸</span>
                  <span className="ctxbar__focus">
                    {selected.numero_intervention || selected.id} · {getInterventionDisplayLines(selected, 'Intervention').headline}
                  </span>
                </>
              ) : (
                <>
                  <span className="ctxbar__sep">▸</span>
                  <span className="ctxbar__focus">Aucune intervention sélectionnée</span>
                </>
              )}
            </div>
            <div className="ctxbar__stats">
              <span className={`stat-pill ${counts.wait ? 'is-red' : ''}`}>Attente <b>{counts.wait}</b></span>
              <span className="stat-pill is-amber">En cours <b>{counts.run}</b></span>
              <span className="stat-pill is-green">Dispo <b>{dispoCount}</b></span>
              <span className="stat-pill is-blue">Engagés <b>{engagedCount}</b></span>
              <span className="stat-pill">Parc <b>{scopedVehicles.length}</b></span>
            </div>
          </div>

          {railActive !== 'dispatch' ? (
            <div className="viewpane" style={{ padding: 10, overflow: 'auto' }}>
              {renderOpsView()}
            </div>
          ) : (
          <main className="cockpit">
            {/* Colonne 1 — File opérationnelle */}
            <section className="panel col-queue">
              <div className="panel__head">
                <h2>File opérationnelle</h2>
                <div className="segmented">
                  <button className={queueFilter === 'wait' ? 'is-active' : ''} onClick={() => setQueueFilter('wait')}>
                    Attente <b>{counts.wait}</b>
                  </button>
                  <button className={queueFilter === 'run' ? 'is-active' : ''} onClick={() => setQueueFilter('run')}>
                    En cours <b>{counts.run}</b>
                  </button>
                  <button className={queueFilter === 'all' ? 'is-active' : ''} onClick={() => setQueueFilter('all')}>
                    Toutes <b>{counts.all}</b>
                  </button>
                </div>
              </div>
              <div className="panel__body scroll">
                {shownQueue.length === 0 ? (
                  <div className="empty" style={{ margin: 10 }}>
                    {priseAppels
                      ? 'Aucune intervention. En attente d’appels.'
                      : 'Prise d’appels désactivée — aucune intervention. Activez-la pour recevoir des appels.'}
                  </div>
                ) : (
                  shownQueue.map((it) => {
                    const lines = getInterventionDisplayLines(it, 'Intervention');
                    const phase = getInterventionPhaseUiKey(it);
                    const p = Number(it.priorite ?? 3);
                    const need = effectiveMoyensRequired(it) || 1;
                    const got = Array.isArray((it as any).dispatch_player_vehicle_ids)
                      ? (it as any).dispatch_player_vehicle_ids.length
                      : 0;
                    const phaseLab = phase === 'wait' ? 'Attente' : phase === 'travel' ? 'Transit' : phase === 'site' ? 'Sur les lieux' : 'En cours';
                    return (
                      <button
                        key={it.id}
                        className={`icard ${selectedId === it.id ? 'is-sel' : ''}`}
                        onClick={() => setSelectedId(it.id)}
                      >
                        <div className="icard__top">
                          <span className="icard__code">{it.numero_intervention || it.id}</span>
                          <span className={`icard__prio p${p}`}>P{p}</span>
                        </div>
                        <div className="icard__title">{lines.headline}</div>
                        <div className="icard__addr">{lines.subline || 'Adresse non précisée'}</div>
                        <div className="icard__foot">
                          <span className={`phase phase--${phase}`}>{phaseLab}</span>
                          <span className="icard__chrono">{elapsed(it)}</span>
                          <span className="icard__spacer" />
                          <span className={`icard__means ${got >= need ? 'is-ok' : ''}`}>{got}/{need}</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </section>

            {/* Colonne 2 — Carte + moniteur d'engins */}
            <section className="col-center">
              <div className={`panel map-panel ${mapNight ? 'map-night' : ''}`}>
                <div className="panel__head panel__head--map">
                  <h2>Carte tactique</h2>
                  <div className="maptools">
                    <button
                      type="button"
                      className={`tbtn ${showSectors ? 'is-active' : ''}`}
                      onClick={() => setShowSectors((v) => !v)}
                    >
                      Secteurs
                    </button>
                    <button type="button" className="tbtn is-active">Engins</button>
                    <button type="button" className="tbtn">Libellés</button>
                    <button
                      type="button"
                      className={`tbtn ${mapNight ? 'is-active' : ''}`}
                      onClick={() => setMapNight((v) => !v)}
                    >
                      Nuit
                    </button>
                    <span className="divider" />
                    <button type="button" className="tbtn">Centrer sélection</button>
                    <button type="button" className="tbtn">Vue générale</button>
                  </div>
                </div>
                <div className="map-wrap">
                  <WorldMap
                    buildings={scopedBuildings}
                    showInterventionSectors={showSectors}
                    interventions={scopedInterventions}
                    liveVehicles={liveVehicleTracks}
                  />
                  <div className="map-legend">
                    <span><i className="dot dot--red" />Intervention</span>
                    <span><i className="dot dot--amber" />Sur les lieux</span>
                    <span><i className="dot dot--blue" />Engin en transit</span>
                    <span><i className="dot dot--green" />Site du joueur</span>
                  </div>
                </div>
              </div>

              <UnitsTable
                playerVehicles={scopedVehicles}
                buildings={scopedBuildings}
                interventions={scopedInterventions}
                liveVehicleTracks={liveVehicleTracks}
                crewStatus={crewStatus}
                staffById={staffById}
                palette={palette}
                selected={selectedUnitIds}
                setSelected={setSelectedUnitIds}
                targetIntervention={selected}
                onEngage={engageUnits}
                engaging={engaging}
                engageError={engageError}
              />
            </section>

            {/* Colonne 3 — Panneau contextuel à onglets */}
            <ContextPanel
              intervention={selected}
              vehicleCount={scopedVehicles.length}
              renderRadio={(id) => <RadioJournalDispatch instanceId={id} />}
            />
          </main>
          )}

          <footer className="statusbar">
            <span className="sb-item"><i className="live-dot" /> {priseAppels ? 'PRISE D’APPELS ON' : 'PRISE D’APPELS OFF'}</span>
            <span className="sb-sep" />
            <span className="sb-item">Engins {dispoCount}/{scopedVehicles.length} disponibles</span>
            <span className="sb-sep" />
            <span className="sb-item">{scopedActiveInterventions.length} intervention(s) active(s)</span>
            <span className="sb-sep" />
            <span className="sb-item sb-alert">{counts.wait > 0 ? `${counts.wait} en attente` : 'Situation nominale'}</span>
            <div className="sb-grow" />
            <span className="sb-radio mono">Effectif : {scopedPersonnel.filter((p) => p.actif).length} agents actifs</span>
          </footer>
        </div>
      </div>

      {/* Tiroir « Gestion » — overlay au-dessus du cockpit (le dispatch reste vivant derrière). */}
      {drawerKey ? (
        <CockpitDrawer title={DRAWER_TITLES[drawerKey] || drawerKey} onClose={closeDrawer}>
          {renderDrawer(drawerKey)}
        </CockpitDrawer>
      ) : null}

      {/* Palette de commandes (Ctrl+K). */}
      <CommandPalette open={paletteOpen} items={paletteItems} onClose={() => setPaletteOpen(false)} />

      {/* Module bâtiment — la modale complète de l'app, ré-habillée au visuel du
          prototype (.oscockpit .bmodal). Ouvert depuis Sites / « Gérer le site ». */}
      {detailBuilding ? (
        <BuildingDetailModal
          building={detailBuilding}
          allBuildings={buildings}
          onClose={() => setDetailBuildingId(null)}
          onBuildingUpdate={() => { /* rechargé au prochain sync carte */ }}
          onBuildingDelete={() => setDetailBuildingId(null)}
        />
      ) : null}
    </div>
  );
};

export default Jeu;
